import { motionOk } from "../kit";

/** Equip fly-in: an item crosses to its socket in this long, and each next one starts this much later (concept §9). */
export const FLY_MS = 260;
export const FLY_STAGGER_MS = 90;
/** The socket's landing pop (squash, ring, sparks) and the upgrade shine run this long (armoury.css keyframes match). */
const POP_MS = 520;
/** How long the "Ready for Level N!" banner stays over the stage. */
const BANNER_MS = 2200;
/** The flying copy starts at this scale when it leaves a smaller tile. */
const FLY_START_SCALE = 0.7;

/** Replays a one-shot CSS animation class (pop, shine): remove, reflow, add, and clear it afterwards. */
export function flash(el: Element | null, cls: string, ms = POP_MS): void {
  if (!el || !motionOk()) return;
  el.classList.remove(cls);
  void (el as HTMLElement).offsetWidth;
  el.classList.add(cls);
  window.setTimeout(() => el.classList.remove(cls), ms);
}

/**
 * Flies a copy of `img` from `from` (a tile or button's box before the re-render) to where `target` now sits, then
 * pops the target. Transform/opacity only (Web Animations), on a fixed-position copy inside `host`; with reduced
 * motion it just pops.
 */
export function flyIn(host: HTMLElement, img: HTMLImageElement | null, from: DOMRect, target: HTMLElement, delayMs: number, onLand?: () => void): void {
  if (!img || !motionOk() || typeof img.animate !== "function") { onLand?.(); return; }
  const to = target.getBoundingClientRect();
  const copy = img.cloneNode(true) as HTMLImageElement;
  copy.className = "arm-fly";
  Object.assign(copy.style, { left: `${to.left}px`, top: `${to.top}px`, width: `${to.width}px`, height: `${to.height}px` });
  const dx = from.left + from.width / 2 - (to.left + to.width / 2), dy = from.top + from.height / 2 - (to.top + to.height / 2);
  const s = Math.min(1, Math.max(FLY_START_SCALE, from.width / Math.max(1, to.width)));
  target.style.opacity = "0";
  host.append(copy);
  const anim = copy.animate([
    { transform: `translate(${dx}px, ${dy}px) scale(${s})`, opacity: 0.9 },
    { transform: `translate(${dx * 0.4}px, ${dy * 0.4 - 30}px) scale(1.1)`, opacity: 1, offset: 0.55 },
    { transform: "none", opacity: 1 },
  ], { duration: FLY_MS, delay: delayMs, easing: "cubic-bezier(.3,.7,.4,1)", fill: "backwards" });
  const land = () => { copy.remove(); target.style.opacity = ""; onLand?.(); };
  anim.onfinish = land;
  anim.oncancel = land;
}

/** The green "Ready for Level N!" banner (or a plain gold one) over the stage, gone by itself. */
export function banner(stage: HTMLElement | null, title: string, sub: string, ready: boolean): void {
  if (!stage) return;
  const b = document.createElement("div");
  b.className = `arm-banner${ready ? " ready" : ""}`;
  b.innerHTML = `<b class="disp"></b><small></small>`;
  b.querySelector("b")!.textContent = title;
  b.querySelector("small")!.textContent = sub;
  stage.append(b);
  window.setTimeout(() => b.classList.add("out"), BANNER_MS);
  window.setTimeout(() => b.remove(), BANNER_MS + POP_MS);
}
